import { getSchoolYearForDate } from '../../../shared/constant/schoolYears';
import { DISBURSED_STATUSES } from '../../../shared/constant/projectStatus';
import { MONTHS } from '../../../shared/constant/months';

export function buildFinancialReport({ payments, projects, students, filters }) {
  const { schoolYear, year, month, gradeLevel, section } = filters;

  const studentById = new Map(students.map((s) => [s.id, s]));

  const filteredPayments = payments.filter((p) => {
    if (gradeLevel && p.grade_level !== gradeLevel) return false;
    const student = p.student_id ? studentById.get(p.student_id) : null;
    if (section && student?.section !== section) return false;
    // Prefer the school year captured on the payment itself — it's frozen at the
    // time of payment, unlike the student's record which can change on promotion.
    const paymentSchoolYear = p.school_year ?? student?.school_year;
    if (schoolYear && paymentSchoolYear !== schoolYear) return false;
    if (year && !String(p.payment_date ?? '').startsWith(year)) return false;
    if (month && String(p.payment_date ?? '').slice(5, 7) !== month) return false;
    return true;
  });

  const filteredProjects = projects.filter((p) => {
    if (schoolYear && getSchoolYearForDate(p.expense_date) !== schoolYear) return false;
    if (year && !String(p.expense_date ?? '').startsWith(year)) return false;
    if (month && String(p.expense_date ?? '').slice(5, 7) !== month) return false;
    return true;
  });

  const categoryTotals = new Map();
  for (const p of filteredPayments) {
    const key = p.category || 'Uncategorized';
    categoryTotals.set(key, (categoryTotals.get(key) ?? 0) + Number(p.amount));
  }
  const collectionsByCategory = [...categoryTotals.entries()]
    .map(([category, amount]) => ({ category, amount }))
    .sort((a, b) => a.category.localeCompare(b.category));
  const totalCollections = filteredPayments.reduce((sum, p) => sum + Number(p.amount), 0);

  const disbursementRows = filteredProjects
    .slice()
    .sort((a, b) => String(a.expense_date ?? '').localeCompare(String(b.expense_date ?? '')))
    .map((p) => ({
      date: p.expense_date,
      name: p.name,
      category: p.category,
      voucherNumber: p.voucher_number,
      recipient: p.recipient,
      amount: Number(p.amount),
      status: p.status,
    }));
  const totalDisbursements = filteredProjects
    .filter((p) => DISBURSED_STATUSES.has(p.status))
    .reduce((sum, p) => sum + Number(p.amount), 0);

  const filterLabels = [];
  if (schoolYear) filterLabels.push(`SY ${schoolYear}`);
  if (year) filterLabels.push(year);
  if (month) filterLabels.push(MONTHS.find((m) => m.value === month)?.label ?? month);
  if (gradeLevel) filterLabels.push(gradeLevel);
  if (section) filterLabels.push(`Section ${section}`);
  const periodLabel = filterLabels.length > 0 ? filterLabels.join(' • ') : 'All Records';

  return {
    periodLabel,
    collectionsByCategory,
    totalCollections,
    disbursementRows,
    totalDisbursements,
    netBalance: totalCollections - totalDisbursements,
    paymentCount: filteredPayments.length,
    projectCount: filteredProjects.length,
  };
}
